// src/pages/Register.js – NextScenes Register (compact direct Reader/Writer signup + Google/Facebook/Apple)
// Build: 2026-05-05-register-compact-direct-writer-v2-1

import React, { useMemo, useState, useRef, useEffect } from "react";
import "../App.css";
import "./RegisterMinimalV1.css";
import { useUser } from "../UserContext";
import { api } from "../api";
import { requestNewsletterAfterSuccessfulRegistration } from "../utils/newsletterRegistrationIntentV1";

const APP_LANG_KEY = "ns.lang";
const INVITE_TOKEN_KEY = "ns.inviteToken";
const INVITE_KIND_KEY = "ns.inviteKind";
const INVITE_CODE_KEY = "ns.inviteCodeForGoogle";
const WRITER_ROLE_KEY = "ns.roleChoiceForGoogle";
const POST_LOGIN_TARGET_KEY = "ns.postLoginTarget";
const PENDING_PROJECT_CATEGORY_KEY = "ns.nav.pendingProjectCategory";
const WRITER_AGREE_KEY = "ns.writerAgreementForGoogle";

function normalizeLang(v) {
  const x = String(v || "").toLowerCase().trim();
  return x.startsWith("fr") ? "fr" : "en";
}

function getUiLang() {
  try {
    const qs = new URLSearchParams(window.location.search || "");
    const qLang = String(qs.get("lang") || "").trim().toLowerCase();
    if (qLang === "fr") return "fr";
    if (qLang === "en") return "en";
  } catch {}

  try {
    const s = String(window.localStorage.getItem(APP_LANG_KEY) || "").trim().toLowerCase();
    if (s === "fr" || s === "en") return s;
  } catch {}

  return "en";
}

function roleLabel(role, lang = "en") {
  const r = String(role || "").toLowerCase();
  const fr = normalizeLang(lang) === "fr";
  if (r === "admin") return "Admin";
  if (r === "moderator") return "Moderator";
  if (r === "writer") return "Writer";
  return fr ? "Lecteur" : "Reader";
}

function isWritingRegistrationRole(role) {
  const r = String(role || "").toLowerCase().trim();
  return r === "writer" || r === "moderator";
}

function roleNameForMessage(role, lang = "en") {
  return roleLabel(role, lang);
}

function normalizeRegisterError(msg, lang) {
  const raw = String(msg || "");
  const m = raw.toLowerCase();
  const fr = lang === "fr";

  if (m.includes("invite-only") || m.includes("invite only") || m.includes("invite code")) {
    return fr
      ? "L’inscription est réservée aux invités pour le moment. Veuillez saisir votre code d’invitation."
      : "Registration is invite-only right now. Please enter your invite code.";
  }
  if (m.includes("invalid invite code")) {
    return fr
      ? "Ce code d’invitation n’est pas valide. Vérifiez le code et réessayez."
      : "That invite code is not valid. Please confirm the code and try again.";
  }

  if (m.includes("email already registered")) {
    return fr
      ? "Cet email est déjà enregistré. Veuillez vous connecter."
      : "This email is already registered. Please log in instead.";
  }

  if (m.includes("username already taken")) {
    return fr
      ? "Ce nom d’utilisateur est déjà pris. Veuillez en choisir un autre."
      : "That username is already taken. Please choose another.";
  }

  if (
    m.includes("uppercase") ||
    m.includes("must be lowercase") ||
    m.includes("lowercase only") ||
    (m.includes("invalid username") && m.includes("lower"))
  ) {
    return fr
      ? "Le nom d’utilisateur doit être en minuscules uniquement. Supprimez les majuscules."
      : "Username must be lowercase only. Please remove capital letters.";
  }

  if (m.includes("invalid username")) {
    return fr
      ? "Nom d’utilisateur invalide. 3–30 caractères : minuscules, chiffres, point ou underscore."
      : "Invalid username. Use 3–30 chars: lowercase letters, numbers, dot or underscore.";
  }

  if (m.includes("password must be at least") || m.includes("password too short")) {
    return fr ? "Le mot de passe doit contenir au moins 8 caractères." : "Password must be at least 8 characters.";
  }
  if (m.includes("invalid email")) {
    return fr
      ? "Email invalide. Vérifiez l’orthographe et réessayez."
      : "Invalid email. Please check the spelling and try again.";
  }

  if (m.includes("invalid role")) {
    return fr ? "Rôle invalide. Choisissez Lecteur, Writer ou Moderator." : "Invalid role selection. Please choose Reader, Writer, or Moderator.";
  }

  if (m.includes("writer/moderator agreement") || m.includes("writer agreement is required")) {
    return fr
      ? "Pour vous inscrire avec un rôle d’écriture, vous devez accepter les conditions Writer/Moderator."
      : "To register with writing access, you must accept the Writer/Moderator conditions.";
  }

  if (m.includes("google sign-in failed") || m.includes("google sign-in could not be completed")) {
    return fr
      ? "La connexion Google n’a pas pu aboutir. Réessayez, ou utilisez votre nom d’utilisateur/email et mot de passe."
      : "Google sign-in could not be completed. Please try again, or use username/email and password.";
  }

  if (m.includes("google login is not configured")) {
    return fr
      ? "La connexion Google n’est pas encore configurée."
      : "Google sign-in is not configured yet.";
  }

  if (m.includes("facebook sign-in failed")) {
    return fr ? "La connexion Facebook a échoué." : "Facebook sign-in failed.";
  }

  if (m.includes("facebook login is not configured")) {
    return fr
      ? "La connexion Facebook n’est pas encore configurée."
      : "Facebook sign-in is not configured yet.";
  }

  if (m.includes("facebook account email missing") || m.includes("facebook account email missing or invalid")) {
    return fr
      ? "Facebook n’a pas fourni d’email vérifié. Vérifiez votre compte Facebook ou utilisez une autre méthode."
      : "Facebook did not provide a verified email. Please check your Facebook account or use another method.";
  }
  if (m.includes("account already exists with this email") || m.includes("facebook linking can be added") || m.includes("facebook linking will be added") || m.includes("not linked to this nextscenes account")) {
    return fr
      ? "Un compte NextScenes existe déjà avec cet email. Connectez-vous d’abord avec votre méthode habituelle, puis liez Facebook depuis le profil. L’email Facebook doit correspondre à l’email NextScenes."
      : "A NextScenes account already exists with this email. Please log in with your usual method first, then link Facebook from Profile. The Facebook email must match your NextScenes email.";
  }


  if (m.includes("apple sign-in") || m.includes("apple account") || m.includes("apple identity")) {
    if (m.includes("already exists with this email")) {
      return fr
        ? "Un compte NextScenes existe déjà avec cet email. Connectez-vous d’abord avec votre méthode habituelle. La liaison Apple pourra être ajoutée ensuite."
        : "A NextScenes account already exists with this email. Please log in first with your usual method. Apple linking can be added later.";
    }
    if (m.includes("not configured")) {
      return fr ? "La connexion Apple n’est pas encore configurée." : "Apple sign-in is not configured yet.";
    }
    return fr
      ? "La connexion Apple n’a pas pu aboutir. Réessayez, ou créez un compte par email."
      : "Apple sign-in could not be completed. Please try again, or create an account by email.";
  }

  return raw || (fr ? "Échec de l’inscription." : "Registration failed.");
}

function bestMessage(ex) {
  return ex?.data?.message || ex?.response?.data?.message || ex?.message || "";
}

function safeTrim(v) {
  return String(v || "").trim();
}

function safeSetLocal(key, value) {
  try {
    window.localStorage.setItem(key, String(value || ""));
  } catch {}
}

function RoleButton({ title, text, badge, selected, disabled, onClick }) {
  return (
    <button
      type="button"
      className={`ns-register-role-card${selected ? " is-selected" : ""}`}
      onClick={onClick}
      disabled={disabled}
      aria-pressed={selected ? "true" : "false"}
    >
      <span className="ns-register-role-badge">{badge}</span>
      <span className="ns-register-role-title">{title}</span>
      <span className="ns-register-role-text">{text}</span>
    </button>
  );
}

function toLowerUsername(v) {
  return String(v || "").toLowerCase();
}

export default function Register({ onNavigate, onNavigatePage, lang }) {
  const {
    register,
    resendVerification,
    refreshMe,
    isAuthenticated,
    user,
    booting,
    role,
  } = useUser();

  const nav =
    typeof onNavigate === "function"
      ? onNavigate
      : typeof onNavigatePage === "function"
        ? onNavigatePage
        : null;

  const GOOGLE_CLIENT_ID = useMemo(
    () => String(process.env.REACT_APP_GOOGLE_CLIENT_ID || "").trim(),
    []
  );

  const FACEBOOK_APP_ID = useMemo(
    () => String(process.env.REACT_APP_FACEBOOK_APP_ID || "").trim(),
    []
  );

  const APPLE_SIGNIN_ENABLED = useMemo(() => {
    return String(process.env.REACT_APP_APPLE_SIGNIN_ENABLED || "true").trim().toLowerCase() !== "false";
  }, []);

  const BACKEND_PUBLIC_URL = useMemo(() => {
    const raw = String(process.env.REACT_APP_BACKEND_PUBLIC_URL || "").trim();
    const fallback = "https://api.nextscenes.org";
    return (raw || fallback).replace(/\/+$/, "");
  }, []);

  const googleBtnRef = useRef(null);
  const googleRedirectTimerRef = useRef(null);
  const usernameInputRef = useRef(null);

  const [uiLang, setUiLang] = useState("en");
  useEffect(() => {
    setUiLang(normalizeLang(lang || getUiLang()));
  }, [lang]);

  const [inviteToken, setInviteToken] =
    useState("");

  const [inviteKind, setInviteKind] =
    useState("");

  useEffect(() => {
    try {
      const sp =
        new URLSearchParams(
          window.location.search || ""
        );

      const tokenFromQuery =
        String(
          sp.get("inviteToken") || ""
        ).trim();

      const token =
        tokenFromQuery ||
        String(
          window.localStorage.getItem(
            INVITE_TOKEN_KEY
          ) || ""
        ).trim();

      if (token) {
        window.localStorage.setItem(
          INVITE_TOKEN_KEY,
          token
        );

        setInviteToken(
          token
        );
      }

      const kindFromQuery =
        String(
          sp.get("inviteKind") || ""
        )
          .trim()
          .toLowerCase();

      if (
        tokenFromQuery &&
        kindFromQuery !== "project"
      ) {
        window.localStorage.removeItem(
          INVITE_KIND_KEY
        );

        setInviteKind("");

        return;
      }

      const kind =
        kindFromQuery ||
        String(
          window.localStorage.getItem(
            INVITE_KIND_KEY
          ) || ""
        )
          .trim()
          .toLowerCase();

      if (
        kind === "project"
      ) {
        window.localStorage.setItem(
          INVITE_KIND_KEY,
          "project"
        );

        setInviteKind(
          "project"
        );
      }
    } catch {}
  }, []);

  useEffect(() => {
    try {
      const sp = new URLSearchParams(window.location.search || "");
      if (sp.get("apple_error")) {
        const msg = sp.get("message") || "Apple sign-in could not be completed.";
        setErr(normalizeRegisterError(msg, normalizeLang(lang || getUiLang())));
        window.history.replaceState({}, "", "/register");
      }
    } catch {}
  }, [lang]);

  useEffect(() => {
    return () => {
      if (googleRedirectTimerRef.current) {
        window.clearTimeout(googleRedirectTimerRef.current);
      }
    };
  }, []);

  const fr = uiLang === "fr";

  const [showInvite, setShowInvite] = useState(false);
  const hasSocialMethods = Boolean(GOOGLE_CLIENT_ID || FACEBOOK_APP_ID || APPLE_SIGNIN_ENABLED);
  // Show one clear sign-up method at a time; role and consent stay visible.
  const [showSocialMethods, setShowSocialMethods] = useState(hasSocialMethods);
  const [showEmailFields, setShowEmailFields] = useState(!hasSocialMethods);
  const toggleEmailSignup = () => {
    const nextEmail = !showEmailFields;
    setShowEmailFields(nextEmail);
    setShowSocialMethods(!nextEmail && hasSocialMethods);
  };
  const [inviteCode, setInviteCode] = useState(() => {
    try {
      return String(window.localStorage.getItem(INVITE_CODE_KEY) || "");
    } catch {
      return "";
    }
  });

  const [username, setUsername] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  // Manual registration only. Optional, unchecked by default, never persisted in the auth payload.
  const [newsletterOptIn, setNewsletterOptIn] = useState(false);
  const [newsletterRequestStatus, setNewsletterRequestStatus] = useState("");

  const [roleChoice, setRoleChoice] = useState(() => {
    try {
      const stored = String(window.localStorage.getItem(WRITER_ROLE_KEY) || "writer").toLowerCase().trim();
      if (stored === "moderator") return "moderator";
      return stored === "user" ? "user" : "writer";
    } catch {
      return "writer";
    }
  });
  const [writerAgree, setWriterAgree] = useState(() => {
    try {
      return String(window.localStorage.getItem(WRITER_AGREE_KEY) || "").toLowerCase() === "true";
    } catch {
      return false;
    }
  });

  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);

  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const [created, setCreatedEmail] = useState(false);
  const [createdEmail, setCreatedEmailValue] = useState("");
  const [resendBusy, setResendBusy] = useState(false);
  const [resendMsg, setResendMsg] = useState("");

  const [googleBusy, setGoogleBusy] = useState(false);
  const [googleReady, setGoogleReady] = useState(false);
  const [googleLoadProblem, setGoogleLoadProblem] = useState("");
  const [googleReloadKey, setGoogleReloadKey] = useState(0);
  const [facebookBusy, setFacebookBusy] = useState(false);
  const [facebookReady, setFacebookReady] = useState(false);
  const [facebookLoadProblem, setFacebookLoadProblem] = useState("");
  const [facebookReloadKey, setFacebookReloadKey] = useState(0);
  const [appleBusy, setAppleBusy] = useState(false);
  const [googleSuccessMsg, setGoogleSuccessMsg] = useState("");

  const displayIdentity = useMemo(() => {
    return user?.name || user?.username || user?.email || "";
  }, [user]);

  const trimmed = useMemo(() => {
    const rc = String(roleChoice || "user").toLowerCase().trim();
    const safeRole = rc === "moderator" ? "moderator" : rc === "writer" ? "writer" : "user";

    return {
      inviteCode: safeTrim(inviteCode),
      username: safeTrim(username),
      name: safeTrim(name),
      email: safeTrim(email),
      password: String(password || ""),
      roleChoice: safeRole,
      writerAgreementAccepted: isWritingRegistrationRole(safeRole) ? !!writerAgree : false,
    };
  }, [inviteCode, username, name, email, password, roleChoice, writerAgree]);

  const needsWriterAgreement = useMemo(() => {
    return isWritingRegistrationRole(trimmed.roleChoice);
  }, [trimmed.roleChoice]);

  const canSubmit = useMemo(() => {
    if (busy || googleBusy || facebookBusy || appleBusy) return false;
    if (!trimmed.username) return false;
    if (!trimmed.email) return false;
    if (!trimmed.password) return false;
    if (needsWriterAgreement && !trimmed.writerAgreementAccepted) return false;
    return true;
  }, [busy, googleBusy, facebookBusy, appleBusy, trimmed, needsWriterAgreement]);

  function persistGoogleIntent() {
    safeSetLocal(WRITER_ROLE_KEY, trimmed.roleChoice);
    safeSetLocal(WRITER_AGREE_KEY, trimmed.writerAgreementAccepted ? "true" : "false");
  }

  function getDestinationFromRole(nextRole) {
    const r = String(nextRole || "").toLowerCase();
    if (r === "admin") return "admin";
    if (r === "writer" || r === "moderator") return "projectHub";
    return "storylines";
  }

  function pendingInviteAcceptUrl() {
    const token =
      String(
        inviteToken || ""
      ).trim();

    if (!token) {
      return "";
    }

    const path =
      inviteKind === "project"
        ? `/api/project-invitations/${encodeURIComponent(
            token
          )}/accept`
        : `/api/stories/group/invite/${encodeURIComponent(
            token
          )}/accept`;

    return (
      `${BACKEND_PUBLIC_URL}${path}`
    );
  }

  function continueAfterSocialAuth(
    destination
  ) {
    const inviteUrl =
      pendingInviteAcceptUrl();

    if (inviteUrl) {
      try {
        window.localStorage.removeItem(
          INVITE_TOKEN_KEY
        );

        window.localStorage.removeItem(
          INVITE_KIND_KEY
        );
      } catch {}

      window.location.assign(
        inviteUrl
      );

      return;
    }

    if (nav) {
      let category = "";
      let target = "";
      try {
        target = String(window.localStorage.getItem(POST_LOGIN_TARGET_KEY) || "");
        const candidate = String(window.localStorage.getItem(PENDING_PROJECT_CATEGORY_KEY) || "");
        if (["story", "children", "textbook", "screenplay"].includes(candidate)) category = candidate;
        window.localStorage.removeItem(POST_LOGIN_TARGET_KEY);
        window.localStorage.removeItem(PENDING_PROJECT_CATEGORY_KEY);
      } catch {}
      if (destination === "projectHub" && target === "projectHub" && category) {
        nav("projectHub", { projectCategory: category });
      } else {
        nav(destination);
      }
    }
  }

  function queueGoogleRedirect(destination) {
    if (!nav && !inviteToken) return;

    if (googleRedirectTimerRef.current) {
      window.clearTimeout(googleRedirectTimerRef.current);
    }

    googleRedirectTimerRef.current = window.setTimeout(() => {
      continueAfterSocialAuth(
        destination
      );
    }, 1400);
  }

  async function handleGoogleCredential(googleResponse) {
    const credential = String(googleResponse?.credential || "").trim();
    if (!credential) {
      setErr(fr ? "La connexion Google n’a pas pu aboutir. Réessayez, ou utilisez votre nom d’utilisateur/email et mot de passe." : "Google sign-in could not be completed. Please try again, or use username/email and password.");
      return;
    }

    if (needsWriterAgreement && !trimmed.writerAgreementAccepted) {
      setErr(
        fr
          ? "Pour vous inscrire avec un rôle d’écriture, vous devez accepter les conditions Writer/Moderator."
          : "To register with writing access, you must accept the Writer/Moderator conditions."
      );
      return;
    }

    persistGoogleIntent();
    setErr("");
    setResendMsg("");
    setGoogleSuccessMsg("");
    setGoogleBusy(true);

    try {

      const data = await api.post(
        "/api/auth/google",
        {
          credential,
          mode: "register",
          inviteCode: trimmed.inviteCode,
          inviteToken:
            inviteToken || undefined,
          roleChoice: trimmed.roleChoice,
          writerAgreementAccepted: trimmed.writerAgreementAccepted,
        },
      );

      const me = await refreshMe();
      const nextRole = String(me?.role || data?.user?.role || "").toLowerCase();
      const destination = getDestinationFromRole(nextRole);

      if (data?.isNewUser) {
        const message = data?.welcomeEmailSent
          ? (fr
              ? `Compte ${roleNameForMessage(nextRole, uiLang)} créé avec Google. Email de bienvenue envoyé.`
              : `${roleNameForMessage(nextRole, uiLang)} account created with Google. Welcome email sent.`)
          : (fr
              ? `Compte ${roleNameForMessage(nextRole, uiLang)} créé avec Google.`
              : `${roleNameForMessage(nextRole, uiLang)} account created with Google.`);

        setGoogleSuccessMsg(message);
        queueGoogleRedirect(destination);
        return;
      }

      if (data?.roleUpgraded) {
        setGoogleSuccessMsg(
          fr
            ? `Votre compte a été mis à jour en ${roleNameForMessage(nextRole, uiLang)}. Redirection…`
            : `Your account has been updated to ${roleNameForMessage(nextRole, uiLang)}. Redirecting…`
        );
        queueGoogleRedirect(destination);
        return;
      }

      continueAfterSocialAuth(
        destination
      );
    } catch (ex) {
      const msg = normalizeRegisterError(bestMessage(ex), uiLang);
      setErr(msg);


    } finally {
      setGoogleBusy(false);
    }
  }

  function retryGoogleSignInLoad() {
    setGoogleReady(false);
    setGoogleLoadProblem("");

    try {
      const existing = document.getElementById("google-identity-services");
      if (existing && !window.google?.accounts?.id) existing.remove();
    } catch {}

    setGoogleReloadKey((n) => n + 1);
  }

  useEffect(() => {
    // Defer Google SDK work until the member opens alternative registration methods.
    if (!showSocialMethods) {
      setGoogleReady(false);
      return;
    }
    if (!GOOGLE_CLIENT_ID) {
      setGoogleReady(false);
      setGoogleLoadProblem("not_configured");
      return;
    }

    let cancelled = false;
    let timer = null;
    let attempts = 0;
    const maxAttempts = 48; // about 12 seconds
    const scriptId = "google-identity-services";

    setGoogleReady(false);
    setGoogleLoadProblem("");

    function stopTimer() {
      if (timer) {
        window.clearInterval(timer);
        timer = null;
      }
    }

    function markProblem(kind) {
      if (cancelled) return;
      setGoogleReady(false);
      setGoogleLoadProblem(kind);
    }

    function renderGoogleButton() {
      if (cancelled) return true;
      if (!window.google?.accounts?.id) return false;
      if (!googleBtnRef.current) return false;

      try {
        googleBtnRef.current.innerHTML = "";

        window.google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: handleGoogleCredential,
        });

        window.google.accounts.id.renderButton(googleBtnRef.current, {
          theme: "outline",
          size: "large",
          shape: "rectangular",
          width: Math.max(190, Math.min(380, Math.floor(googleBtnRef.current?.getBoundingClientRect().width || 340), Math.floor((googleBtnRef.current?.parentElement?.clientWidth || 380) - 20), Math.floor(window.innerWidth - 96))),
          text: "signup_with",
          locale: normalizeLang(uiLang) === "fr" ? "fr" : "en",
        });

        setGoogleReady(true);
        setGoogleLoadProblem("");
        return true;
      } catch (err) {
        console.warn("Google signup button failed to render:", err);
        markProblem("failed");
        return true;
      }
    }

    function waitForGoogle() {
      if (renderGoogleButton()) return;

      stopTimer();
      timer = window.setInterval(() => {
        attempts += 1;

        if (renderGoogleButton()) {
          stopTimer();
          return;
        }

        if (attempts >= maxAttempts) {
          stopTimer();
          markProblem("slow");
        }
      }, 250);
    }

    const existing = document.getElementById(scriptId);

    if (!existing) {
      const script = document.createElement("script");
      script.id = scriptId;
      script.src = `https://accounts.google.com/gsi/client?hl=${normalizeLang(uiLang)}`;
      script.async = true;
      script.defer = true;
      script.onload = () => waitForGoogle();
      script.onerror = () => markProblem("failed");
      document.body.appendChild(script);
    } else {
      waitForGoogle();
    }

    return () => {
      cancelled = true;
      stopTimer();
    };
  }, [GOOGLE_CLIENT_ID, uiLang, roleChoice, writerAgree, inviteCode, googleReloadKey, showSocialMethods]);

  function retryFacebookSignInLoad() {
    setFacebookReady(false);
    setFacebookLoadProblem("");

    try {
      const existing = document.getElementById("facebook-jssdk");
      if (existing && !window.FB) existing.remove();
    } catch {}

    setFacebookReloadKey((n) => n + 1);
  }

  useEffect(() => {
    if (!FACEBOOK_APP_ID) {
      setFacebookReady(false);
      setFacebookLoadProblem("not_configured");
      return undefined;
    }

    let cancelled = false;
    let timer = null;
    let attempts = 0;
    const maxAttempts = 48; // about 12 seconds
    const scriptId = "facebook-jssdk";
    const locale = normalizeLang(uiLang) === "fr" ? "fr_FR" : "en_US";

    setFacebookReady(false);
    setFacebookLoadProblem("");

    function stopTimer() {
      if (timer) {
        window.clearInterval(timer);
        timer = null;
      }
    }

    function markProblem(kind) {
      if (cancelled) return;
      setFacebookReady(false);
      setFacebookLoadProblem(kind);
    }

    function initFacebook() {
      if (cancelled) return true;
      if (!window.FB) return false;

      try {
        window.FB.init({
          appId: FACEBOOK_APP_ID,
          cookie: false,
          xfbml: false,
          version: "v25.0",
        });

        setFacebookReady(true);
        setFacebookLoadProblem("");
        return true;
      } catch (err) {
        console.warn("Facebook signup SDK failed to initialize:", err);
        markProblem("failed");
        return true;
      }
    }

    function waitForFacebook() {
      if (initFacebook()) return;

      stopTimer();
      timer = window.setInterval(() => {
        attempts += 1;

        if (initFacebook()) {
          stopTimer();
          return;
        }

        if (attempts >= maxAttempts) {
          stopTimer();
          markProblem("slow");
        }
      }, 250);
    }

    if (!document.getElementById("facebook-root")) {
      const root = document.createElement("div");
      root.id = "facebook-root";
      document.body.appendChild(root);
    }

    const existing = document.getElementById(scriptId);

    if (!existing) {
      const script = document.createElement("script");
      script.id = scriptId;
      script.src = `https://connect.facebook.net/${locale}/sdk.js`;
      script.async = true;
      script.defer = true;
      script.crossOrigin = "anonymous";
      script.onload = () => waitForFacebook();
      script.onerror = () => markProblem("failed");
      document.body.appendChild(script);
    } else {
      waitForFacebook();
    }

    return () => {
      cancelled = true;
      stopTimer();
    };
  }, [FACEBOOK_APP_ID, uiLang, facebookReloadKey]);

  async function handleFacebookLogin() {
    if (!FACEBOOK_APP_ID || !window.FB) {
      setErr(fr ? "La connexion Facebook n’est pas encore configurée." : "Facebook sign-in is not configured yet.");
      return;
    }

    if (needsWriterAgreement && !trimmed.writerAgreementAccepted) {
      setErr(
        fr
          ? "Pour vous inscrire avec un rôle d’écriture, vous devez accepter les conditions Writer/Moderator."
          : "To register with writing access, you must accept the Writer/Moderator conditions."
      );
      return;
    }

    persistGoogleIntent();
    setErr("");
    setResendMsg("");
    setGoogleSuccessMsg("");
    setFacebookBusy(true);

    window.FB.login(
      (facebookResponse) => {
        (async () => {
          try {
            const authResponse = facebookResponse?.authResponse || {};
            const accessToken = String(authResponse.accessToken || "").trim();
            const userID = String(authResponse.userID || "").trim();

            if (!accessToken) {
              throw new Error("Facebook sign-in failed.");
            }


            const data = await api.post(
              "/api/auth/facebook",
              {
                accessToken,
                userID,
                mode: "register",
                inviteCode: trimmed.inviteCode,
                inviteToken:
                  inviteToken || undefined,
                roleChoice: trimmed.roleChoice,
                writerAgreementAccepted: trimmed.writerAgreementAccepted,
              },
            );

            const me = await refreshMe();
            const nextRole = String(me?.role || data?.user?.role || "").toLowerCase();
            const destination = getDestinationFromRole(nextRole);

            if (data?.isNewUser) {
              const message = data?.welcomeEmailSent
                ? (fr
                    ? `Compte ${roleNameForMessage(nextRole, uiLang)} créé avec Facebook. Email de bienvenue envoyé.`
                    : `${roleNameForMessage(nextRole, uiLang)} account created with Facebook. Welcome email sent.`)
                : (fr
                    ? `Compte ${roleNameForMessage(nextRole, uiLang)} créé avec Facebook.`
                    : `${roleNameForMessage(nextRole, uiLang)} account created with Facebook.`);

              setGoogleSuccessMsg(message);
              queueGoogleRedirect(destination);
              return;
            }

            if (data?.roleUpgraded) {
              setGoogleSuccessMsg(
                fr
                  ? `Votre compte a été mis à jour en ${roleNameForMessage(nextRole, uiLang)}. Redirection…`
                  : `Your account has been updated to ${roleNameForMessage(nextRole, uiLang)}. Redirecting…`
              );
              queueGoogleRedirect(destination);
              return;
            }

            continueAfterSocialAuth(
              destination
            );
          } catch (ex) {
            const msg = normalizeRegisterError(bestMessage(ex), uiLang);
            setErr(msg);
          } finally {
            setFacebookBusy(false);
          }
        })();
      },
      { scope: "public_profile,email", return_scopes: true }
    );
  }


  function handleAppleLogin() {
    if (!APPLE_SIGNIN_ENABLED) {
      setErr(fr ? "La connexion Apple n’est pas encore configurée." : "Apple sign-in is not configured yet.");
      return;
    }

    if (needsWriterAgreement && !trimmed.writerAgreementAccepted) {
      setErr(
        fr
          ? "Pour vous inscrire avec un rôle d’écriture, vous devez accepter les conditions Writer/Moderator."
          : "To register with writing access, you must accept the Writer/Moderator conditions."
      );
      return;
    }

    persistGoogleIntent();
    setErr("");
    setResendMsg("");
    setGoogleSuccessMsg("");
    setAppleBusy(true);

    try {
      const url = new URL(`${BACKEND_PUBLIC_URL}/api/auth/apple/start`);
      url.searchParams.set("mode", "register");
      if (trimmed.inviteCode) url.searchParams.set("inviteCode", trimmed.inviteCode);
      if (inviteToken) url.searchParams.set("inviteToken", inviteToken);
      if (inviteKind === "project") {
        url.searchParams.set(
          "inviteKind",
          "project"
        );
      }
      if (trimmed.roleChoice) url.searchParams.set("roleChoice", trimmed.roleChoice);
      url.searchParams.set("writerAgreementAccepted", trimmed.writerAgreementAccepted ? "true" : "false");
      window.location.assign(url.toString());
    } catch (ex) {
      setAppleBusy(false);
      setErr(fr ? "La connexion Apple n’a pas pu démarrer." : "Apple sign-in could not be started.");
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();

    if (!canSubmit) {
      if (needsWriterAgreement && !trimmed.writerAgreementAccepted) {
        setErr(
          fr
            ? "Pour vous inscrire avec un rôle d’écriture, vous devez accepter les conditions Writer/Moderator."
            : "To register with writing access, you must accept the Writer/Moderator conditions."
        );
      }
      return;
    }

    setErr("");
    setResendMsg("");
    setGoogleSuccessMsg("");
    setBusy(true);

    try {
      const payload = {
        inviteCode: trimmed.inviteCode,
        inviteToken:
          inviteToken || undefined,
        username: trimmed.username,
        name: trimmed.name,
        email: trimmed.email,
        password: trimmed.password,
        roleChoice: trimmed.roleChoice,
        writerAgreementAccepted: trimmed.writerAgreementAccepted,
      };

      const data = await register(payload);

      if (data?.needsVerification) {
        setCreatedEmail(true);
        setCreatedEmailValue(trimmed.email);
        setPassword("");
        if (newsletterOptIn) {
          setNewsletterRequestStatus("pending");
          // This is separate from account verification. It cannot affect signup success.
          void requestNewsletterAfterSuccessfulRegistration({
            consent: true, email: trimmed.email, language: uiLang
          }, (newsletterPayload) => api.post("/api/newsletter/subscribe", newsletterPayload))
            .then((result) => setNewsletterRequestStatus(result.status));
        }
        return;
      }

      if (nav) nav("login");
    } catch (ex) {
      const msg = normalizeRegisterError(bestMessage(ex), uiLang);
      setErr(msg);


    } finally {
      setBusy(false);
    }
  }

  async function handleResend() {
    const em = safeTrim(createdEmail || email);
    if (!em) return;

    setResendMsg("");
    setResendBusy(true);
    try {
      await resendVerification({ email: em });
      setResendMsg(
        fr
          ? "Email de vérification renvoyé. Vérifiez votre boîte (et les spams)."
          : "Verification email sent again. Please check your inbox (and spam)."
      );
    } catch (ex) {
      setResendMsg(bestMessage(ex) || (fr ? "Échec de renvoi de l’email." : "Failed to resend verification email."));
    } finally {
      setResendBusy(false);
    }
  }

  function pickRole(nextRole) {
    const raw = String(nextRole || "").toLowerCase().trim();
    const r = raw === "moderator" ? "moderator" : raw === "writer" ? "writer" : "user";
    setRoleChoice(r);

    if (!isWritingRegistrationRole(r)) setWriterAgree(false);
    if (err && (String(err).toLowerCase().includes("writer") || String(err).toLowerCase().includes("moderator"))) setErr("");
    if (googleSuccessMsg) setGoogleSuccessMsg("");

    safeSetLocal(WRITER_ROLE_KEY, r);
    if (!isWritingRegistrationRole(r)) {
      safeSetLocal(WRITER_AGREE_KEY, "false");
    }
  }

  function handleUsernameChange(e) {
    const el = e.target;
    const raw = String(el.value || "");

    const start = el.selectionStart;
    const end = el.selectionEnd;

    const lowered = toLowerUsername(raw);
    setUsername(lowered);

    requestAnimationFrame(() => {
      const input = usernameInputRef.current;
      if (!input) return;
      if (typeof start === "number" && typeof end === "number") {
        try {
          input.setSelectionRange(start, end);
        } catch {}
      }
    });

    if (err && String(err).toLowerCase().includes("lowercase")) setErr("");
    if (err && String(err).toLowerCase().includes("capital")) setErr("");
    if (err && String(err).toLowerCase().includes("minuscul")) setErr("");
    if (err && String(err).toLowerCase().includes("majuscul")) setErr("");
  }

  if (booting) {
    return (
      <div className="page">
        <div className="card" style={{ maxWidth: 560, margin: "0 auto" }}>
          <h2 style={{ marginTop: 0 }}>{fr ? "Vérification de session…" : "Checking session…"}</h2>
          <p style={{ marginTop: 8, color: "#475569" }}>{fr ? "Un instant." : "One moment."}</p>
        </div>
      </div>
    );
  }

  if (isAuthenticated) {
    return (
      <div className="page">
        <div className="card" style={{ maxWidth: 560, margin: "0 auto" }}>
          <h2 style={{ marginTop: 0 }}>
            {fr ? "Vous avez déjà une session active" : "You already have an active session"}
          </h2>
          <p style={{ marginTop: 8 }}>
            {fr ? "Connecté en tant que" : "Logged in as"} <b>{displayIdentity}</b>
          </p>
          <p style={{ marginTop: 8, color: "#475569" }}>
            {fr ? "Rôle" : "Role"}: <b>{roleLabel(user?.role || role, uiLang)}</b>
          </p>
          <button
            className="btn"
            onClick={() => nav && nav("home")}
            style={{ marginTop: 12 }}
            type="button"
          >
            {fr ? "Accueil" : "Go Home"}
          </button>
        </div>
      </div>
    );
  }

  if (created) {
    return (
      <div className="page">
        <div className="card" style={{ maxWidth: 560, margin: "0 auto" }}>
          <h2 style={{ marginTop: 0 }}>{fr ? "Compte créé" : "Account created"}</h2>

          <div className="banner" style={{ marginTop: 10 }}>
            {fr ? (
              <>
                Nous avons envoyé un lien de vérification à <b>{createdEmail}</b>. <br />
                Veuillez vérifier votre email avant de vous connecter.
              </>
            ) : (
              <>
                We sent a verification link to <b>{createdEmail}</b>. <br />
                Please verify your email before logging in.
              </>
            )}
          </div>

          {newsletterRequestStatus ? (
            <div role="status" style={{ padding: "11px 14px", marginTop: 12, borderRadius: 10,
              background: "#edf5ea", color: "#24573b", lineHeight: 1.6 }}>
              {newsletterRequestStatus === "pending"
                ? (fr ? "Demande d’abonnement à la newsletter en cours…" : "Requesting your optional newsletter confirmation…")
                : newsletterRequestStatus === "request_accepted"
                ? (fr ? "Si votre adresse est admissible, un e-mail séparé vous invitera à confirmer votre abonnement facultatif à la newsletter."
                    : "If eligible, check your inbox for a separate optional newsletter confirmation email.")
                : (fr ? "Votre compte a bien été créé. La newsletter est indisponible pour le moment ; vous pourrez vous abonner plus tard depuis le pied de page."
                    : "Your account was created successfully. Newsletter sign-up is unavailable right now; you can subscribe later from the footer.")}
            </div>
          ) : null}

          <p style={{ marginTop: 10, color: "#475569" }}>
            {fr
              ? "Si vous ne voyez pas le message, vérifiez les spams ou l’onglet promotions."
              : "If you don’t see the message, check your spam or promotions folder."}
          </p>

          {resendMsg ? (
            <div className="banner" style={{ marginTop: 10, background: "#dcfce7" }}>
              {resendMsg}
            </div>
          ) : null}

          <div style={{ marginTop: 12, display: "flex", gap: 10, flexWrap: "wrap" }}>
            <button className="btn" type="button" onClick={() => nav && nav("login")}>
              {fr ? "Aller à Connexion" : "Go to Login"}
            </button>

            <button className="btn" type="button" onClick={handleResend} disabled={resendBusy}>
              {resendBusy ? (fr ? "Renvoi..." : "Resending...") : fr ? "Renvoyer l’email" : "Resend verification email"}
            </button>

            <button
              className="linkBtn"
              type="button"
              onClick={() => nav && nav("home")}
              style={{ fontWeight: 800 }}
            >
              {fr ? "Accueil" : "Go Home"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  const selectedRoleName = roleNameForMessage(roleChoice, uiLang);

  const writerAgreementText = fr
    ? `En choisissant ${selectedRoleName}, vous confirmez que vous respecterez les standards NextScenes de narration respectueuse. Vous acceptez que les histoires sur NextScenes puissent être collaboratives, modifiées selon les modes de la plateforme, et conservées avec un historique transparent. Si vous choisissez Moderator, vous acceptez aussi d’agir comme mentor avec intégrité, respect, patience et responsabilité envers les personnes que vous guidez. Vous vous engagez à ne pas publier de contenu volé, haineux ou nuisible, et vous comprenez que les abus graves peuvent entraîner une restriction ou une exclusion.`
    : `By choosing ${selectedRoleName}, you confirm that you will uphold NextScenes standards of respectful storytelling. You accept that stories written on NextScenes may be collaborative, edited through the platform’s modes, and preserved with transparent history. If you choose Moderator, you also agree to mentor with integrity, respect, patience, and responsibility toward the people you guide. You agree not to post stolen work, hateful content, or harmful material, and you understand that serious abuse can lead to restriction or removal.`;

  const copy = {
    eyebrow: fr ? "Compte NextScenes" : "NextScenes Account",
    title: fr ? "Rejoindre NextScenes" : "Join NextScenes",
    intro: fr
      ? "Écrivez, lisez et découvrez."
      : "Write, read, and explore.",
    inviteOptional: fr ? "Optionnel" : "Optional",
    inviteHint: fr
      ? "Utilisez ce champ seulement si NextScenes, une école, un club ou un groupe privé vous a donné un code. Sinon, continuez sans code."
      : "Use this only if NextScenes, a school, club, or private group gave you a code. Otherwise, continue without one.",
    inviteInputHint: fr
      ? "Saisissez le code uniquement si un code vous a été donné. Sinon, laissez ce champ vide."
      : "Enter a code only if one was given to you. Otherwise, leave this field empty.",
    hideInvite: fr ? "Masquer le code d’invitation" : "Hide invite code",
    showInvite: fr ? "Vous avez un code d’invitation ?" : "Have an invite code?",
    roleTitle: fr ? "Choisissez votre chemin" : "Choose your path",
    roleIntro: fr
      ? "Writer crée. Moderator guide. Reader explore."
      : "Writer creates. Moderator guides. Reader explores.",
    writerTitle: fr ? "Writer" : "Writer",
    writerBadge: fr ? "Créer" : "Create",
    writerText: fr
      ? "Créez des storylines et des scènes."
      : "Create storylines and scenes.",
    moderatorTitle: fr ? "Moderator" : "Moderator",
    moderatorBadge: fr ? "Guider" : "Mentor",
    moderatorText: fr
      ? "Accès Writer, avec responsabilité de mentorat pour guider des auteurs."
      : "Writer access, plus mentoring responsibility for guiding writers.",
    readerTitle: fr ? "Reader" : "Reader",
    readerBadge: fr ? "Lire" : "Read",
    readerText: fr
      ? "Lisez et découvrez."
      : "Read and discover.",
    selectedPath: fr ? "Chemin sélectionné" : "Selected path",
    writerNote: fr
      ? "Le statut Writer exige de protéger l’originalité, de respecter les autres et de garder le Canon."
      : "Writer status requires agreement to protect originality, respect others, and guard the Canon.",
    moderatorNote: fr
      ? "Le statut Moderator donne l’accès Writer et ajoute une responsabilité de mentorat envers les auteurs guidés."
      : "Moderator status gives Writer access and adds mentoring responsibility toward guided writers.",
    readerNote: fr
      ? "Les Readers peuvent explorer maintenant et devenir Writers plus tard depuis Story Hub."
      : "Readers can browse now and become Writers later from Story Hub.",
    writerConditionsSummary: fr ? "Lire les conditions Writer/Moderator" : "Review Writer/Moderator conditions",
    googleTitle: fr ? "Continuer avec Google" : "Continue with Google",
    googleText: fr
      ? "Aucune vérification email NextScenes."
      : "No NextScenes email verification.",
    googleWriterReminder: fr
      ? "Pour créer un compte Writer ou Moderator avec Google, cochez d’abord les conditions Writer/Moderator."
      : "To create a Writer or Moderator account with Google, accept the Writer/Moderator conditions first.",
    googleReadyNote: fr
      ? "Aucun clic supplémentaire sur “Créer un compte” n’est nécessaire."
      : "No additional click on “Create Account” is needed.",
    facebookTitle: fr ? "Continuer avec Facebook" : "Continue with Facebook",
    facebookText: fr
      ? "Aucune vérification email NextScenes."
      : "No NextScenes email verification.",
    facebookWriterReminder: fr
      ? "Pour créer un compte Writer ou Moderator avec Facebook, cochez d’abord les conditions Writer/Moderator."
      : "To create a Writer or Moderator account with Facebook, accept the Writer/Moderator conditions first.",
    facebookReadyNote: fr
      ? "Facebook doit fournir un email pour créer ou connecter le compte."
      : "Facebook must provide an email to create or connect the account.",
    appleTitle: fr ? "Continuer avec Apple" : "Continue with Apple",
    appleText: fr
      ? "Utilisez votre identifiant Apple pour créer ou retrouver votre compte NextScenes."
      : "Use your Apple ID to create or return to your NextScenes account.",
    appleWriterReminder: fr
      ? "Pour créer un compte Writer ou Moderator avec Apple, cochez d’abord les conditions Writer/Moderator."
      : "To create a Writer or Moderator account with Apple, accept the Writer/Moderator conditions first.",
    appleReadyNote: fr
      ? "Apple ouvrira une page sécurisée, puis vous ramènera à NextScenes."
      : "Apple opens a secure sign-in page, then returns you to NextScenes.",
    divider: fr ? "OU S’INSCRIRE PAR EMAIL" : "OR SIGN UP WITH EMAIL",
    manualTitle: fr ? "Email et mot de passe" : "Email and password",
    manualIntro: fr
      ? "Vérification requise."
      : "Verification required.",
  };

  return (
    <div className="page ns-register-page ns-register-minimal-v1">
      <div className="ns-register-shell">
        <section className="ns-register-hero" aria-label={copy.title} style={{ padding: "16px 22px" }}>
          <div>
            <div className="ns-register-eyebrow">{copy.eyebrow}</div>
            <h1>{copy.title}</h1>
            <p>{copy.intro}</p>
          </div>
          <button
            className="btn ns-register-cancel"
            type="button"
            disabled={busy || googleBusy || facebookBusy || appleBusy}
            onClick={() => nav && nav("home")}
          >
            {fr ? "Annuler" : "Cancel"}
          </button>
        </section>

        <div className="ns-register-card" style={{ padding: 18 }}>
          <div className="ns-register-invite-panel" style={{ padding: 10, marginBottom: 10 }}>
            <div className="ns-register-invite-row">
              <button
                type="button"
                className="linkBtn ns-register-invite-toggle"
                disabled={busy || googleBusy || facebookBusy || appleBusy}
                onClick={() => setShowInvite((v) => !v)}
                title={fr ? "Codes d’invitation pour les testeurs privés" : "Invite codes are for private testers"}
              >
                {showInvite ? copy.hideInvite : copy.showInvite}
              </button>
            </div>
          </div>


          {err ? (
            <div className="banner ns-register-banner" style={{ marginTop: 12 }}>
              {err}
            </div>
          ) : null}

          {googleSuccessMsg ? (
            <div className="banner ns-register-banner" style={{ marginTop: 12, background: "#dcfce7" }}>
              {googleSuccessMsg}
            </div>
          ) : null}

          <form onSubmit={handleSubmit} style={{ marginTop: 10 }}>
            {showInvite ? (
              <div className="ns-register-field-block">
                <label className="label">{fr ? "Code d’invitation" : "Invite Code"}</label>
                <input
                  className="input"
                  value={inviteCode}
                  onChange={(e) => {
                    const v = e.target.value;
                    setInviteCode(v);
                    safeSetLocal(INVITE_CODE_KEY, v);
                  }}
                  placeholder={fr ? "Saisissez le code" : "Enter invite code"}
                  autoComplete="off"
                  disabled={busy || googleBusy || facebookBusy || appleBusy}
                />
              </div>
            ) : null}

            <section className="ns-register-section ns-register-role-section" style={{ padding: 12, marginTop: 10 }}>
              <div className="ns-register-section-head">
                <div>
                  <h2>{copy.roleTitle}</h2>
                </div>
              </div>

              <div className="ns-register-role-grid" style={{ gap: 10 }}>
                <RoleButton
                  title={copy.writerTitle}
                  badge={copy.writerBadge}
                  text={copy.writerText}
                  selected={roleChoice === "writer"}
                  disabled={busy || googleBusy || facebookBusy || appleBusy}
                  onClick={() => pickRole("writer")}
                />
                <RoleButton
                  title={copy.moderatorTitle}
                  badge={copy.moderatorBadge}
                  text={copy.moderatorText}
                  selected={roleChoice === "moderator"}
                  disabled={busy || googleBusy || facebookBusy || appleBusy}
                  onClick={() => pickRole("moderator")}
                />
                <RoleButton
                  title={copy.readerTitle}
                  badge={copy.readerBadge}
                  text={copy.readerText}
                  selected={roleChoice === "user"}
                  disabled={busy || googleBusy || facebookBusy || appleBusy}
                  onClick={() => pickRole("user")}
                />
              </div>

              {isWritingRegistrationRole(roleChoice) ? (
                <div className="ns-register-writer-box" style={{ marginTop: 8, padding: 10 }}>
                  <details className="ns-register-writer-details">
                    <summary>{copy.writerConditionsSummary}</summary>
                    <div>{writerAgreementText}</div>
                  </details>

                  <label className="ns-register-checkbox-row">
                    <input
                      type="checkbox"
                      checked={writerAgree}
                      disabled={busy || googleBusy || facebookBusy || appleBusy}
                      onChange={(e) => {
                        const checked = !!e.target.checked;
                        setWriterAgree(checked);
                        safeSetLocal(WRITER_AGREE_KEY, checked ? "true" : "false");
                      }}
                    />
                    <span>
                      {fr
                        ? "J’ai lu et j’accepte les conditions Writer/Moderator."
                        : "I have read and agree to the Writer/Moderator conditions."}
                    </span>
                  </label>

                </div>
              ) : null}
            </section>

            <div className="ns-register-alternatives">
              {showSocialMethods ? (
                <p className="ns-register-methods-heading">{fr ? "Créer un compte avec" : "Sign up with"}</p>
              ) : null}
              {showSocialMethods ? (
                <div id="ns-register-social-methods" className="ns-register-social-options">
                  {GOOGLE_CLIENT_ID ? (
                    <div className="ns-register-social-provider" aria-label="Google">
                      <div ref={googleBtnRef} className="ns-register-google-button" />
                      {!googleReady && !googleLoadProblem ? (
                        <div className="ns-register-loading-note">{fr ? "Chargement de Google…" : "Loading Google…"}</div>
                      ) : null}
                      {!googleReady && googleLoadProblem ? (
                        <div className="ns-social-provider-fallback" role="status">
                          <span>{fr ? "Google est temporairement indisponible." : "Google is temporarily unavailable."}</span>
                          {googleLoadProblem !== "not_configured" ? (
                            <button type="button" className="ns-social-retry-button" onClick={retryGoogleSignInLoad}>
                              {fr ? "Réessayer" : "Retry"}
                            </button>
                          ) : null}
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                  {FACEBOOK_APP_ID ? (
                    <div className="ns-register-social-provider">
                      <button className="ns-facebook-login-button ns-facebook-register-button"
                        type="button" onClick={handleFacebookLogin}
                        aria-label={fr ? "Continuer avec Facebook" : "Continue with Facebook"}
                        disabled={busy || googleBusy || facebookBusy || appleBusy || !facebookReady}>
                        <span aria-hidden="true">f</span>
                        {facebookBusy
                          ? (fr ? "Connexion…" : "Connecting…")
                          : (fr ? "Continuer avec Facebook" : "Continue with Facebook")}
                      </button>
                      {!facebookReady && facebookLoadProblem ? (
                        <div className="ns-social-provider-fallback" role="status">
                          <span>{fr ? "Facebook est temporairement indisponible." : "Facebook is temporarily unavailable."}</span>
                          {facebookLoadProblem !== "not_configured" ? (
                            <button type="button" className="ns-social-retry-button" onClick={retryFacebookSignInLoad}>
                              {fr ? "Réessayer" : "Retry"}
                            </button>
                          ) : null}
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                  {APPLE_SIGNIN_ENABLED ? (
                    <div className="ns-register-social-provider">
                      <button className="ns-facebook-login-button ns-facebook-register-button ns-apple-register-button"
                        type="button" onClick={handleAppleLogin}
                        aria-label={fr ? "Continuer avec Apple" : "Continue with Apple"}
                        disabled={busy || googleBusy || facebookBusy || appleBusy}>
                        <span aria-hidden="true" style={{ fontFamily: "Georgia,serif", fontWeight: 900 }}></span>
                        {appleBusy
                          ? (fr ? "Connexion…" : "Connecting…")
                          : (fr ? "Continuer avec Apple" : "Continue with Apple")}
                      </button>
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>

            {hasSocialMethods ? (
              <button type="button"
                className="ns-register-email-choice"
                aria-expanded={showEmailFields}
                aria-controls="ns-register-email-fields"
                disabled={busy || googleBusy || facebookBusy || appleBusy}
                onClick={toggleEmailSignup}>
                {showEmailFields
                  ? (fr ? "Autres options d’inscription" : "Other sign-up options")
                  : (fr ? "Continuer avec un e-mail" : "Continue with email")}
              </button>
            ) : null}

            {showEmailFields ? (
            <section id="ns-register-email-fields" className="ns-register-section ns-register-manual-section" style={{ padding: 12, marginTop: 10 }}>
              <div className="ns-register-section-head">
                <div>
                  <h2>{copy.manualTitle}</h2>
                </div>
              </div>

              <label className="label" style={{ marginTop: 14 }}>
                {fr ? "Nom d’utilisateur (obligatoire)" : "Username (required)"}
              </label>
              <input
                ref={usernameInputRef}
                className="input"
                value={username}
                onChange={handleUsernameChange}
                placeholder="buddy.writer"
                autoComplete="username"
                required
                disabled={busy || googleBusy || facebookBusy || appleBusy}
                inputMode="text"
                spellCheck={false}
                aria-describedby="username-help"
              />
              <div id="username-help" className="ns-register-help-text">
                {fr ? "Minuscules uniquement, ex. dimgba_01" : "Lowercase only, e.g. dimgba_01"}
              </div>

              <label className="label" style={{ marginTop: 12 }}>
                {fr ? "Nom (optionnel)" : "Name (optional)"}
              </label>
              <input
                className="input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={fr ? "Votre nom d’affichage" : "Your display name"}
                autoComplete="name"
                disabled={busy || googleBusy || facebookBusy || appleBusy}
              />

              <label className="label" style={{ marginTop: 12 }}>
                Email
              </label>
              <input
                className="input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                autoComplete="email"
                required
                disabled={busy || googleBusy || facebookBusy || appleBusy}
              />

              <div className="ns-register-password-row">
                <div>
                  <label className="label">{fr ? "Mot de passe (min 8 caractères)" : "Password (min 8 chars)"}</label>
                  <input
                    className="input"
                    type={showPw ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={fr ? "Créez un mot de passe" : "Create a password"}
                    autoComplete="new-password"
                    required
                    disabled={busy || googleBusy || facebookBusy || appleBusy}
                  />
                </div>

                <button
                  type="button"
                  className="linkBtn ns-register-show-password"
                  onClick={() => setShowPw((v) => !v)}
                  disabled={busy || googleBusy || facebookBusy || appleBusy}
                  aria-label={showPw ? (fr ? "Masquer" : "Hide password") : fr ? "Afficher" : "Show password"}
                  title={showPw ? (fr ? "Masquer" : "Hide password") : fr ? "Afficher" : "Show password"}
                >
                  {showPw ? (fr ? "Masquer" : "Hide") : fr ? "Afficher" : "Show"}
                </button>
              </div>

              <label
                data-newsletter-registration-consent-v1="true"
                style={{ display: "flex", gap: 10, alignItems: "flex-start",
                  padding: "13px 14px", margin: "15px 0 12px",
                  borderRadius: 12, border: "1px solid #cdddcf",
                  background: "#f4f7f1", color: "#2d5840", lineHeight: 1.5 }}
              >
                <input type="checkbox" checked={newsletterOptIn}
                  onChange={(e) => setNewsletterOptIn(e.target.checked)}
                  disabled={busy || googleBusy || facebookBusy || appleBusy}
                  style={{ marginTop: 4, accentColor: "#0f5f35" }}/>
                <span>
                  <strong>{fr ? "Recevoir les actualités NextScenes (facultatif)" : "Receive NextScenes updates (optional)"}</strong>
                  <span style={{ display: "block", fontSize: 13, marginTop: 3 }}>
                    {fr
                      ? "Actualités NextScenes occasionnelles. Confirmation par e-mail ; désabonnement à tout moment."
                      : "Occasional NextScenes news. Confirm by email; unsubscribe anytime."}
                  </span>
                </span>
              </label>

              <button className="btn ns-register-submit" type="submit" disabled={!canSubmit}>
                {busy
                  ? (fr ? "Création..." : "Creating...")
                  : fr
                    ? "Créer un compte avec email"
                    : "Create Account with Email"}
              </button>

            </section>
            ) : null}
          </form>

          <div className="ns-register-login-row">
            {fr ? "Vous avez déjà un compte ?" : "Already have an account?"}{" "}
            <button className="linkBtn" onClick={() => nav && nav("login")} type="button" disabled={busy || googleBusy || facebookBusy || appleBusy}>
              {fr ? "Connexion" : "Login"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
